import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { showError, showInfo } from '../../shared/ui/appToast';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import { useScreenRefresh } from '../../shared/ui/hooks-screen/useScreenRefresh';
import { useFocusEffect } from '@react-navigation/native';
import { invalidateBranchSalesListCaches } from '../infra/salesListInvalidation';
import { useSalesListForBranch } from '../screen-hooks/useSalesListForBranch';
import { useSalesListKpis } from '../screen-hooks/useSalesListKpis';
import { buildSalesListCsv } from '../infra/salesListCsv';
import {
  fetchAllSalesListRowsForExport,
  SALES_LIST_EXPORT_MAX_ROWS,
} from '../infra/salesListExport';
import { shareOrDownloadCsv } from '../../shared/csv-core/infra/shareCsv';
import {
  formatLocalYmd,
  getLocalYmdRangeFromPreset,
  matchPosSalesHistoryDatePreset,
  ymdToEndIso,
  ymdToStartIso,
} from '../infra/salesDateRange';
import {
  buildSalesListCashierOptions,
  SALES_LIST_CASHIER_NULL_KEY,
} from '../../cash/labels-api';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { useEmployeesStore, withSelfOwner } from '../../team/store/useEmployeesStore';
import {
  buildAuthUserLabelById,
  resolveAuthUserDisplayLabel,
} from '../../auth/domain/authUserDisplayLabels';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { useBranchPermission } from '../../shell/session/hooks/useBranchPermission';
import type { DateRangePreset } from '../infra/salesReportingConstants';
import type { SaleListRow } from '../domain/salesTypes';
import type { SalesListRouteParams } from './salesNavigation';
import {
  resolveSalesListStateFromRouteParams,
  salesListRouteStateToReportsQueryScope,
  type BranchSalesHistoryCashierFilter,
} from './salesListRouteParams';
import { branchHasOnlineSales } from '../infra/branchHasOnlineSales';

export type { BranchSalesHistoryCashierFilter };

export type { SaleStatusFilter, SaleFiscalStatusFilter, SaleSortKey, SaleSortDir, SaleChannelFilter, SaleCommissionFilter } from '../domain/salesTypes';
import type { SaleStatusFilter, SaleFiscalStatusFilter, SaleSortKey, SaleSortDir, SaleChannelFilter, SaleCommissionFilter } from '../domain/salesTypes';

export type UseSalesListScreenOptions = {
  branchId: string | null | undefined;
  initialParams?: SalesListRouteParams;
};

function cashierFilterToListParam(f: BranchSalesHistoryCashierFilter): string | undefined {
  if (f === 'all') return undefined;
  return f;
}

function resolveInitialDates(params?: SalesListRouteParams): { fromYmd: string; toYmd: string } {
  const ymd = formatLocalYmd(new Date());
  return {
    fromYmd: params?.fromYmd?.trim() || ymd,
    toYmd: params?.toYmd?.trim() || ymd,
  };
}

export function useSalesListScreen({ branchId, initialParams }: UseSalesListScreenOptions) {
  const canExport = useModulePermission('POS', 'export');
  const initialDates = useMemo(() => resolveInitialDates(initialParams), [initialParams]);
  const employeesRaw = useEmployeesStore((state) => state.employees);
  const selfOwner = useEmployeesStore((state) => state.selfOwner);
  // Incluye la membresía owner propia: el dueño aparece en filtros/labels de operador.
  const employees = useMemo(() => withSelfOwner(employeesRaw, selfOwner), [employeesRaw, selfOwner]);
  const loadEmployees = useEmployeesStore((state) => state.loadEmployees);
  const sessionUser = useAuthStore((state) => state.session?.user ?? null);
  const ownerAuthUserId = sessionUser?.id ?? null;

  const cashierDisplayName = useMemo(
    () => {
      const labelById = buildAuthUserLabelById({
        sessionUserId: sessionUser?.id,
        sessionUserEmail: sessionUser?.email,
        ownerAuthUserId,
        employees,
      });
      return (userId: string | null) =>
        resolveAuthUserDisplayLabel(userId, labelById, ownerAuthUserId);
    },
    [sessionUser, ownerAuthUserId, employees]
  );

  const [draftFromYmd, setDraftFromYmd] = useState(initialDates.fromYmd);
  const [draftToYmd, setDraftToYmd] = useState(initialDates.toYmd);
  const [appliedFromYmd, setAppliedFromYmd] = useState(initialDates.fromYmd);
  const [appliedToYmd, setAppliedToYmd] = useState(initialDates.toYmd);
  const [search, setSearch] = useState(initialParams?.search ?? '');
  const [appliedCashierFilter, setAppliedCashierFilter] = useState<BranchSalesHistoryCashierFilter>(
    initialParams?.cashierFilter ?? 'all'
  );
  const [appliedPrimaryStaffFilter, setAppliedPrimaryStaffFilter] =
    useState<BranchSalesHistoryCashierFilter>('all');
  const [statusFilter, setStatusFilter] = useState<SaleStatusFilter>('active');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('all');
  const [fiscalStatusFilter, setFiscalStatusFilter] = useState<SaleFiscalStatusFilter>('all');
  const [channelFilter, setChannelFilter] = useState<SaleChannelFilter>('all');
  const [commissionFilter, setCommissionFilter] = useState<SaleCommissionFilter>('all');
  const hasCommissionsCapability = useHasBusinessCapability('commissions');
  const commissionsPermission = useBranchPermission('Commissions');
  const canViewCommissions = hasCommissionsCapability && commissionsPermission.canView;
  const [hasOnlineSalesHistory, setHasOnlineSalesHistory] = useState(false);
  const [sortKey, setSortKey] = useState<SaleSortKey>('created_at');
  const [sortDir, setSortDir] = useState<SaleSortDir>('desc');
  const [paymentPendingOnly, setPaymentPendingOnly] = useState(initialParams?.paymentPendingOnly === true);
  const routeDerived = useMemo(
    () => resolveSalesListStateFromRouteParams(initialParams),
    [initialParams]
  );
  const reportsQueryScope = useMemo(
    () =>
      salesListRouteStateToReportsQueryScope({
        fromYmd: appliedFromYmd,
        toYmd: appliedToYmd,
        search,
        cashierFilter: appliedCashierFilter,
        paymentPendingOnly,
        reportsScope: routeDerived.reportsScope,
      }),
    [
      appliedFromYmd,
      appliedToYmd,
      search,
      appliedCashierFilter,
      paymentPendingOnly,
      routeDerived.reportsScope,
    ]
  );

  useEffect(() => {
    setDraftFromYmd(routeDerived.fromYmd);
    setDraftToYmd(routeDerived.toYmd);
    setAppliedFromYmd(routeDerived.fromYmd);
    setAppliedToYmd(routeDerived.toYmd);
    setSearch(routeDerived.search);
    setAppliedCashierFilter(routeDerived.cashierFilter);
    setPaymentPendingOnly(routeDerived.paymentPendingOnly);
  }, [routeDerived]);

  useEffect(() => {
    void loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    let cancelled = false;
    if (!branchId) {
      setHasOnlineSalesHistory(false);
      return;
    }
    void branchHasOnlineSales(branchId).then((has) => {
      if (!cancelled) setHasOnlineSalesHistory(has);
    });
    return () => {
      cancelled = true;
    };
  }, [branchId]);

  const cashierOptions = useMemo(
    () =>
      branchId
        ? buildSalesListCashierOptions(employees, ownerAuthUserId, sessionUser?.email)
        : [],
    [branchId, employees, ownerAuthUserId, sessionUser?.email]
  );

  /** Misma lista de empleados que Operador, sin "Sin operador". */
  const primaryStaffOptions = useMemo(
    () => cashierOptions.filter((o) => o.key !== SALES_LIST_CASHIER_NULL_KEY),
    [cashierOptions]
  );

  useEffect(() => {
    setAppliedCashierFilter((prev) => {
      if (prev === 'all') return prev;
      return cashierOptions.some((o) => o.key === prev) ? prev : 'all';
    });
  }, [cashierOptions]);

  useEffect(() => {
    setAppliedPrimaryStaffFilter((prev) => {
      if (prev === 'all') return prev;
      return primaryStaffOptions.some((o) => o.key === prev) ? prev : 'all';
    });
  }, [primaryStaffOptions]);

  const getDateRange = useCallback(
    () => ({ fromIso: ymdToStartIso(appliedFromYmd), toIso: ymdToEndIso(appliedToYmd) }),
    [appliedFromYmd, appliedToYmd]
  );

  const {
    rows,
    loading,
    hasMore,
    pageIndex,
    totalCount,
    loadError,
    goToNextPage,
    goToPreviousPage,
    goToPage,
    reload,
    pageSize,
    setPageSize,
  } = useSalesListForBranch({
    branchId,
    getDateRange,
    search,
    cashierUserIdFilter: cashierFilterToListParam(appliedCashierFilter),
    primaryStaffUserIdFilter: cashierFilterToListParam(appliedPrimaryStaffFilter),
    paymentPendingOnly,
    queryScope: reportsQueryScope,
    statusFilter,
    paymentMethodFilter: paymentMethodFilter === 'all' ? undefined : paymentMethodFilter,
    fiscalStatusFilter,
    channelFilter,
    commissionFilter: canViewCommissions ? commissionFilter : 'all',
    sortKey,
    sortDir,
  });

  const { fromIso: kpiFromIso, toIso: kpiToIso } = getDateRange();
  const { kpis: serverKpis, refreshKpis } = useSalesListKpis({
    branchId,
    fromIso: kpiFromIso,
    toIso: kpiToIso,
    cashierUserIdFilter: cashierFilterToListParam(appliedCashierFilter),
    primaryStaffUserIdFilter: cashierFilterToListParam(appliedPrimaryStaffFilter),
    paymentPendingOnly,
    commissionFilter: canViewCommissions ? commissionFilter : 'all',
    statusFilter,
    paymentMethodFilter: paymentMethodFilter === 'all' ? undefined : paymentMethodFilter,
    fiscalStatusFilter,
    channelFilter,
    queryScope: reportsQueryScope,
    enabled: Boolean(branchId),
  });

  const activeDatePreset = useMemo(
    () => matchPosSalesHistoryDatePreset(appliedFromYmd, appliedToYmd),
    [appliedFromYmd, appliedToYmd]
  );

  const refreshSalesList = useCallback(async () => {
    if (!branchId) return;
    invalidateBranchSalesListCaches(branchId);
    await Promise.all([reload(), refreshKpis()]);
  }, [branchId, reload, refreshKpis]);

  const refreshSalesListRef = useRef(refreshSalesList);
  refreshSalesListRef.current = refreshSalesList;

  useFocusEffect(
    useCallback(() => {
      void refreshSalesListRef.current();
    }, [branchId]),
  );

  const { refreshing, onRefresh } = useScreenRefresh(async () => {
    if (!branchId) return { silent: true };
    invalidateBranchSalesListCaches(branchId);
    const [, , hasOnline] = await Promise.all([
      reload(),
      refreshKpis(),
      branchHasOnlineSales(branchId),
    ]);
    setHasOnlineSalesHistory(hasOnline);
  });

  const onApplyDateFilters = useCallback(() => {
    setAppliedFromYmd(draftFromYmd);
    setAppliedToYmd(draftToYmd);
  }, [draftFromYmd, draftToYmd]);

  const onPreset = useCallback((preset: DateRangePreset) => {
    const { fromYmd, toYmd } = getLocalYmdRangeFromPreset(preset);
    setDraftFromYmd(fromYmd);
    setDraftToYmd(toYmd);
    setAppliedFromYmd(fromYmd);
    setAppliedToYmd(toYmd);
  }, []);

  const clearListFilters = useCallback(() => {
    const ymd = formatLocalYmd(new Date());
    setSearch('');
    setDraftFromYmd(ymd);
    setDraftToYmd(ymd);
    setAppliedFromYmd(ymd);
    setAppliedToYmd(ymd);
    setAppliedCashierFilter('all');
    setAppliedPrimaryStaffFilter('all');
    setStatusFilter('active');
    setPaymentMethodFilter('all');
    setFiscalStatusFilter('all');
    setChannelFilter('all');
    setCommissionFilter('all');
    setPaymentPendingOnly(false);
    setSortKey('created_at');
    setSortDir('desc');
  }, []);

  const hasActiveListFilters = useMemo(() => {
    const ymd = formatLocalYmd(new Date());
    return (
      search.trim().length > 0 ||
      appliedCashierFilter !== 'all' ||
      appliedPrimaryStaffFilter !== 'all' ||
      appliedFromYmd !== ymd ||
      appliedToYmd !== ymd ||
      statusFilter !== 'active' ||
      paymentMethodFilter !== 'all' ||
      fiscalStatusFilter !== 'all' ||
      channelFilter !== 'all' ||
      paymentPendingOnly ||
      commissionFilter !== 'all'
    );
  }, [appliedCashierFilter, appliedPrimaryStaffFilter, appliedFromYmd, appliedToYmd, search, statusFilter, paymentMethodFilter, fiscalStatusFilter, channelFilter, paymentPendingOnly, commissionFilter]);

  /**
   * Badge del botón Filtros: solo filtros del sheet (operador, estado, medio,
   * fiscal, canal, pendientes, comisión). Período, búsqueda y orden tienen
   * controles propios visibles — no cuentan acá.
   */
  const activeListFilterCount = useMemo(() => {
    let count = 0;
    if (appliedCashierFilter !== 'all') count++;
    if (appliedPrimaryStaffFilter !== 'all') count++;
    if (statusFilter !== 'active') count++;
    if (paymentMethodFilter !== 'all') count++;
    if (fiscalStatusFilter !== 'all') count++;
    if (channelFilter !== 'all') count++;
    if (paymentPendingOnly) count++;
    if (commissionFilter !== 'all') count++;
    return count;
  }, [appliedCashierFilter, appliedPrimaryStaffFilter, statusFilter, paymentMethodFilter, fiscalStatusFilter, channelFilter, paymentPendingOnly, commissionFilter]);

  const exportCsvRows = useCallback(async (exportRows: SaleListRow[], truncated: boolean) => {
    if (exportRows.length === 0) {
      showInfo('Exportar CSV', 'No hay ventas para exportar con esos filtros.');
      return;
    }
    try {
      const enrichedRows = exportRows.map((row) => ({
        ...row,
        cashierDisplayName: cashierDisplayName(row.cashierUserId),
      }));
      const csv = buildSalesListCsv(enrichedRows);
      await shareOrDownloadCsv(csv, 'ventas_pos.csv');
      if (truncated) {
        showInfo(
          'Exportar CSV',
          `Exporté las primeras ${SALES_LIST_EXPORT_MAX_ROWS.toLocaleString('es-AR')} ventas.`
        );
      }
    } catch (e) {
      captureOpsFailure({
        module: 'sales',
        op: 'sales.export_csv_share',
        error: e,
        ingestError: e instanceof Error ? e.message : null,
        applied: false,
      });
      showError('Exportar CSV', 'No se pudo generar el archivo. Reintentá.');
    }
  }, [cashierDisplayName]);

  const onExportCsv = useCallback(async () => {
    if (!branchId) return;
    if (!canExport) {
      showError('Sin permisos', 'No tenés permiso para exportar ventas.');
      return;
    }
    const { fromIso, toIso } = getDateRange();
    try {
      const { rows: exportRows, truncated } = await fetchAllSalesListRowsForExport({
        branchId,
        fromIso,
        toIso,
        search,
        cashierUserIdFilter: cashierFilterToListParam(appliedCashierFilter),
        primaryStaffUserIdFilter: cashierFilterToListParam(appliedPrimaryStaffFilter),
        paymentPendingOnly,
        queryScope: reportsQueryScope,
        pageSize,
        statusFilter,
        paymentMethodFilter: paymentMethodFilter === 'all' ? undefined : paymentMethodFilter,
        fiscalStatusFilter,
        channelFilter,
        commissionFilter: canViewCommissions ? commissionFilter : 'all',
        sortKey,
        sortDir,
      });
      await exportCsvRows(exportRows, truncated);
    } catch (e) {
      captureOpsFailure({
        module: 'sales',
        op: 'sales.export_csv_fetch',
        error: e,
        ingestError: e instanceof Error ? e.message : null,
        applied: false,
        extra: { branchId },
      });
      showError('Exportar CSV', 'No se pudo leer las ventas. Reintentá.');
    }
  }, [
    appliedCashierFilter,
    appliedPrimaryStaffFilter,
    branchId,
    canExport,
    exportCsvRows,
    getDateRange,
    pageSize,
    paymentPendingOnly,
    reportsQueryScope,
    search,
    statusFilter,
    paymentMethodFilter,
    fiscalStatusFilter,
    channelFilter,
    commissionFilter,
    canViewCommissions,
    sortKey,
    sortDir,
  ]);

  return {
    draftFromYmd,
    draftToYmd,
    appliedFromYmd,
    appliedToYmd,
    setDraftFromYmd,
    setDraftToYmd,
    onApplyDateFilters,
    onPreset,
    activeDatePreset,
    search,
    setSearch,
    rows,
    loading,
    pageIndex,
    pageSize,
    setPageSize,
    totalCount,
    hasMore,
    goToPreviousPage: goToPreviousPage,
    goToNextPage: goToNextPage,
    goToPage,
    reloadList: refreshSalesList,
    onRefresh,
    refreshing,
    onExportCsv,
    canExport,
    cashierFilter: appliedCashierFilter,
    cashierOptions,
    onCashierFilterChange: setAppliedCashierFilter,
    primaryStaffFilter: appliedPrimaryStaffFilter,
    primaryStaffOptions,
    onPrimaryStaffFilterChange: setAppliedPrimaryStaffFilter,
    statusFilter,
    onStatusFilterChange: setStatusFilter,
    paymentMethodFilter,
    onPaymentMethodFilterChange: setPaymentMethodFilter,
    fiscalStatusFilter,
    onFiscalStatusFilterChange: setFiscalStatusFilter,
    channelFilter,
    onChannelFilterChange: setChannelFilter,
    commissionFilter,
    onCommissionFilterChange: setCommissionFilter,
    canViewCommissions,
    sortKey,
    sortDir,
    onSortChange: useCallback((key: SaleSortKey, dir: SaleSortDir) => {
      setSortKey(key);
      setSortDir(dir);
    }, []),
    paymentPendingOnly,
    onPaymentPendingOnlyChange: setPaymentPendingOnly,
    activeListFilterCount,
    hasActiveListFilters,
    clearListFilters,
    loadError,
    serverKpis,
    /** True if this branch ever had channel=online sales (tienda apagada inclusive). */
    hasOnlineSalesHistory,
  };
}

export type SalesListScreenViewModel = ReturnType<typeof useSalesListScreen>;
